import { CARD_TYPE_OBJ, FLEX_CVC_FIELD_TYPE, FLEX_NUMBER_FIELD_TYPE } from '~/common/utils/consts';
import { action, computed, observable, reaction } from 'mobx';
import * as Sentry from '@sentry/react';
import axios from 'axios';

import JWTDecode from '~/common/utils/JWTDecode';
import { NOT_SUPPORTED_CREDIT_CARD_TYPE } from '~/common/utils/errorMessaging';
import { gtm, errorFormEvent } from '~/common/utils/tracking';
import fatalErrorModal from '~/common/utils/fatalErrorModal.js';

import { formsStore } from '../ui/forms';
import { modalStore } from '../ui/modal';

class FlexFrameStoreClass {
    @observable key = '';
    @observable token = ''
    @observable microform = {};
    @observable cardField = null;
    @observable codeField = null;
    @observable isCardValid = false;
    @observable isCodeValid = false;
    @observable isCardFieldFocused = false;
    @observable isCodeFieldFocused = false;
    @observable isCardSupported = true;
    @observable userInteractedWithCardField = false;
    @observable userInteractedWithCodeField = false;
    @observable creatingMicroform = false;
    @observable cardHasValue = false;
    @observable codeHasValue = false;
    @observable cardType = null;
    @observable codeLabel = 'CVV';
    @observable differentValidCardTypesDetected = false;
    @observable validCardTypesDetected = [];
    @observable flexFrameError = '';
    @observable FingerPrintID = '';

    bootstrapStore() {
        // react to key creation, when token avalible, create microform
        reaction(
            // object to watch
            () => this.key,
            // create microform
            () => this.createMicroForm()
        );

        // react to card type information
        reaction(
            () => this.cardType,
            (cardType) => {
                // if card type was set activate valid card observable
                if (cardType !== '') {
                    this.setIsCardValid(true);
                    if (!this.isCardSupported) this.setIsCardSupported(true);
                } else {
                    this.setIsCardValid(false);
                    if (this.isCardSupported) this.setIsCardSupported(false);
                }
            }
        );

        // react to more than one valid card
        reaction(
            () => this.differentValidCardTypesDetected,
            (moreThanOneType) => {
                if (moreThanOneType) {
                    modalStore.setModalOptions({
                        isModalOpen: true,
                        isModalClosable: false,
                        modalType: 'large',
                        childComponentString: 'creditCardType'
                    });
                } else if (modalStore.isModalOpen) modalStore.setModalOptions({ isModalOpen: false });
            }
        );
    }

    // setter functions

    @action setKey(key) {
        if (typeof key === 'string') {
            this.key = key;
        }
    }

    @action removeKey() {
        this.key = '';
    }

    @action setToken(token) {
        if (typeof token === 'string') {
            this.token = token;
        }
    }

    @action setMicroform(form) {
        if (typeof form === 'object') {
            this.microform = form;
        }
    }

    @action setCardField(field) {
        if (typeof field === 'object') {
            this.cardField = field;
        }
    }

    @action setCodeField(field) {
        if (typeof field === 'object') {
            this.codeField = field;
        }
    }

    @action setIsCardValid(val) {
        if (typeof val === 'boolean') {
            this.isCardValid = val;
        }
    }

    @action setIsCodeValid(val) {
        if (typeof val === 'boolean') {
            this.isCodeValid = val;
        }
    }

    @action setIsCardSupported(bool) {
        if (typeof bool === 'boolean') {
            this.isCardSupported = bool;
        }
    }

    @action setCreatingMicroform(bool) {
        if (typeof bool === 'boolean') {
            this.creatingMicroform = bool;
        }
    }

    @action setIsCardFieldFocused(bool) {
        if (typeof bool === 'boolean') {
            this.isCardFieldFocused = bool;
        }
    }

    @action setIsCodeFieldFocused(bool) {
        if (typeof bool === 'boolean') {
            this.isCodeFieldFocused = bool;
        }
    }

    @action setUserInteractedWithCardField(bool) {
        if (typeof bool === 'boolean') {
            this.userInteractedWithCardField = bool;
        }
    }

    @action setUserInteractedWithCodeField(bool) {
        if (typeof bool === 'boolean') {
            this.userInteractedWithCodeField = bool;
        }
    }

    @action setCardHasValue(bool) {
        if (typeof bool === 'boolean') {
            this.cardHasValue = bool;
        }
    }

    @action setCodeHasValue(bool) {
        if (typeof bool === 'boolean') {
            this.codeHasValue = bool;
        }
    }

    @action setFlexFrameError(string) {
        if (typeof string === 'string') {
            this.flexFrameError = string;
        }
    }

    @action setCodeLabel(string) {
        if (typeof string === 'string') {
            this.codeLabel = string.toUpperCase();
        }
    }

    @action setCardType(string) {
        if (typeof string === 'string') {
            this.cardType = string;
        }
    }

    @action setDifferentValidCardTypesDetected(bool) {
        if (typeof bool === 'boolean') {
            this.differentValidCardTypesDetected = bool;
        }
    }

    @action setValidCardTypesDetected(arr) {
        if (Array.isArray(arr)) {
            this.validCardTypesDetected = arr;
        }
    }

    @action turnOnFieldsError(fields, callErrorEvent) {
        if (Array.isArray(fields)) {
            if (callErrorEvent && fields.length === 2) {
                gtm(errorFormEvent('buy-form', 'number,securityCode', 'Please enter a valid credit card number,Please enter a valid security code'));
            }
            fields.forEach((errorField) => {
                if (errorField === FLEX_NUMBER_FIELD_TYPE) {
                    this.setIsCardValid(false);
                    this.setUserInteractedWithCardField(true);
                    if (callErrorEvent) {
                        gtm(errorFormEvent('buy-form', 'number', 'Please enter a valid credit card number'));
                    }
                }

                if (errorField === FLEX_CVC_FIELD_TYPE) {
                    this.setIsCodeValid(false);
                    this.setUserInteractedWithCodeField(true);
                    if (callErrorEvent) {
                        gtm(errorFormEvent('buy-form', 'securityCode', 'Please enter a valid security code'));
                    }
                }
            });
        }
    }

    @action getFingerPrintID() {
        this.FingerPrintID = `${new Date().getTime()}_${Math.floor(Math.random() * 100000)}`;
    }

    // get key to create the microform
    @action getFlexKey(callback) {
        axios.get(`/api/flexmicroform?baseURL=${window.location.origin}`).then((resp) => {
            if (callback) {
                callback(resp.data);
            } else {
                this.setKey(resp.data.RawJwt);
            }
        }).catch(() => {
            // couldn't get key, can't sign up
            this.setCreatingMicroform(false);
            fatalErrorModal();
        });
    }

    // create microform
    @action createMicroForm() {
        const fieldGeneralStyles = {
            styles: {
                input: {
                    'font-size': '18px',
                    'font-family': 'Figtree, sans-serif',
                    'font-weight': '400',
                    color: '#000'
                },
                ':focus': { color: '#000' },
                valid: { color: '#000' },
                invalid: { color: '#E60502' }
            }
        };

        try {
            /* eslint-disable */
            const flexEnv = new Flex(this.key);
            /* eslint-enable */

            const microformInstance = flexEnv.microform('card', fieldGeneralStyles);
            this.setMicroform(microformInstance);
        } catch (err) {
            this.setCreatingMicroform(false);
            fatalErrorModal();
            Sentry.captureMessage('flexFrameError', { extra: {
                error: 'Setup Error',
                errorMessage: err
            } });
            return;
        }

        this.initializeFields();
        this.setCreatingMicroform(false);
    }

    @action initializeFields() {
        try {
            // Create fields
            this.setCardField(this.microform.createField(FLEX_NUMBER_FIELD_TYPE));
            // The default max-length is 3 for the verification code
            this.setCodeField(this.microform.createField(FLEX_CVC_FIELD_TYPE));

            // Attach events
            this.cardField.on('focus', () => {
                this.setIsCardFieldFocused(true);
            });

            this.cardField.on('blur', () => {
                this.setIsCardFieldFocused(false);
                if (!this.userInteractedWithCardField) this.setUserInteractedWithCardField(true);

                if (!this.isCardSupported) {
                    this.setFlexFrameError(NOT_SUPPORTED_CREDIT_CARD_TYPE);
                } else {
                    this.setFlexFrameError('');
                }
                formsStore.setCheckoutFormValid('card', this.displayCardErrorUI);
            });

            this.cardField.on('change', (data) => {
                this.setCardHasValue(!data?.empty);
                if (data?.valid) {
                    this.determineCardType(data?.card);
                    this.setIsCardValid(true);
                }
            });

            this.codeField.on('focus', () => {
                this.setIsCodeFieldFocused(true);
            });

            this.codeField.on('blur', () => {
                this.setIsCodeFieldFocused(false);
                if (!this.userInteractedWithCodeField) this.setUserInteractedWithCodeField(true);
                formsStore.setCheckoutFormValid('cvc', this.displayCodeErrorUI);
            });

            this.codeField.on('change', (data) => {
                this.setCodeHasValue(!data?.empty);
                this.setIsCodeValid(data.valid);
            });

            this.cardField.load('#cc__number');
            this.codeField.load('#cvc__number');
        } catch (err) {
            fatalErrorModal();
            Sentry.captureMessage('flexFrameError', { extra: {
                error: 'Setup Error',
                errorMessage: err
            } });
            this.setCreatingMicroform(false);
        }
    }

    @action determineCardType(detectedCards) {
        const validTypesDetected = this.getValidCardTypes(detectedCards);

        if (validTypesDetected.length === 0) {
            this.resetCardTypeInfo();
            return;
        }

        if (validTypesDetected.length === 1) {
            const cardType = validTypesDetected[0].cardType;
            const codeLabel = validTypesDetected[0].codeLabel;
            const codeLength = validTypesDetected[0].codeLength;

            this.setCardTypeInfo(cardType, codeLabel, codeLength);
            this.setDifferentValidCardTypesDetected(false);
            return;
        }

        if (validTypesDetected.length > 1) {
            this.setValidCardTypesDetected(validTypesDetected);
            this.setDifferentValidCardTypesDetected(true);
        }
    }

    getValidCardTypes(detectedCards) {
        const validTypesDetected = [];
        if (Array.isArray(detectedCards) && detectedCards.length > 0) {
            detectedCards.forEach((typeObj) => {
                if (CARD_TYPE_OBJ[typeObj.cybsCardType]) {
                    validTypesDetected.push(
                        {
                            cardType: typeObj.cybsCardType,
                            cardName: typeObj.brandedName,
                            codeLabel: typeObj.securityCode.name,
                            codeLength: typeObj.securityCode.length
                        }
                    );
                }
            });
        }

        return validTypesDetected;
    }

    @action setCardTypeInfo(cardType, codeLabel, codeLength) {
        this.setCardType(cardType);
        this.setCodeLabel(codeLabel);
        this.codeField.update({
            maxLength: codeLength
        });
    }
    @action resetCardTypeInfo() {
        this.setCardType('');
        this.setCodeLabel('CVV');
        this.setDifferentValidCardTypesDetected(false);
    }

    @action destroyFlexFrame() {
        // destroys the microform fields
        if (this.cardField) this.cardField.dispose();
        if (this.codeField) this.codeField.dispose();
    }

    // destroy current microform and create a new one
    @action refreshMicroform() {
        // destroys the microform
        if (this.microform) {
            this.destroyFlexFrame();
            this.getFlexKey();
            this.setIsCardValid(false);
            this.setIsCodeValid(false);
        }
    }

    // on form submit create auth token with card information
    @action createToken(options, successCallback, errCallback) {
        this.microform.createToken(options, (err, token) => {
            // if error
            if (err) {
                errCallback(err);
                return;
            }
            // save token
            this.setToken(token);
            successCallback();
        });
    }


    // computed values

    @computed get displayCardErrorUI() {
        return !this.isCardValid && !this.isCardFieldFocused && this.userInteractedWithCardField;
    }

    @computed get displayCodeErrorUI() {
        return !this.isCodeValid && !this.isCodeFieldFocused && this.userInteractedWithCodeField;
    }

    @computed get decodedToken() {
        return JWTDecode(this.token);
    }

    @computed get tokenString() {
        return JSON.stringify(this.decodedToken);
    }

    @computed get maskCard() {
        return this.decodedToken?.content?.paymentInformation?.card?.number?.maskedValue;
    }

    @computed get finalCardType() {
        // Return the card type detected in the backend of cybersource
        return this.decodedToken?.content.paymentInformation.card.type.value;
    }
}

const flexFrameStore = new FlexFrameStoreClass();
export { flexFrameStore, FlexFrameStoreClass };
